import { useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Alert } from '@/components/ui/alert'
import { EmptyState } from '@/components/ui/empty-state'
import { ReportPage } from '@/components/templates/report-page'
import { RegistrationEventPicker } from '@/components/events/registration-event-picker'
import { RegistrationSummary } from '@/components/events/registration-comparison-figures'
import { RegistrationTable } from '@/components/events/registration-comparison-table'
import { RegistrationChart } from '@/components/events/registration-chart'
import { RegistrationToolbar } from '@/components/events/registration-comparison-toolbar'
import {
  registrationQuery,
  useRegistrationQuery,
  type RegistrationQueryControls,
} from '@/lib/registration-comparison-query'
import { ApiError, getJSON } from '@/lib/api'
import type {
  RegistrationComparison,
  RegistrationComparisonResponse,
} from '@/lib/registration-comparison'
import { NoAccessPage } from './NoAccessPage'

type EventNavigation = { onOpenEvent?: (id: number) => void }

export function EventRegistrationsPage({ onOpenEvent }: EventNavigation = {}) {
  const controls = useRegistrationQuery()
  const eventId = controls.params.get('event_id') ?? ''
  const search = registrationQuery(controls.params)
  const query = useQuery({
    queryKey: ['registration-comparison', eventId, search],
    queryFn: () =>
      getJSON<RegistrationComparisonResponse>(
        `/api/v1/events/${encodeURIComponent(eventId)}/registration_comparison${search ? `?${search}` : ''}`,
      ),
    enabled: Boolean(eventId),
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === eventId ? previous : undefined,
    retry: false,
  })
  const [lastResult, setLastResult] = useState<{
    eventId: string
    report: RegistrationComparison
  }>()
  useEffect(() => {
    if (query.data) setLastResult({ eventId, report: query.data.registration_comparison })
  }, [query.data, eventId])
  const report =
    query.data?.registration_comparison ??
    (lastResult?.eventId === eventId ? lastResult.report : undefined)
  if (query.error instanceof ApiError && query.error.status === 403) return <NoAccessPage />
  return (
    <ReportPage
      title="Event registrations"
      controls={
        <>
          <RegistrationEventPicker
            selectedLabel={report && `${report.event.name} · ${report.event.start_date_label}`}
            onSelect={(id) => controls.update('event_id', id)}
          />
          {eventId && (
            <EventDetailsLink eventId={eventId} onOpenEvent={onOpenEvent}>
              Open event
            </EventDetailsLink>
          )}
        </>
      }
    >
      {!eventId ? (
        <div className="mt-6">
          <EmptyState title="Choose an event" />
        </div>
      ) : (
        <ReportBody
          report={report}
          loading={query.isLoading}
          updating={query.isFetching}
          error={query.error}
          onOpenEvent={onOpenEvent}
          {...controls}
        />
      )}
    </ReportPage>
  )
}

function ReportBody({
  report,
  loading,
  updating,
  error,
  onOpenEvent,
  params,
  update,
}: {
  report?: RegistrationComparison
  loading: boolean
  updating: boolean
  error: Error | null
} & EventNavigation &
  RegistrationQueryControls) {
  if (loading && !report)
    return (
      <p role="status" className="mt-6 text-body-sm text-muted-foreground">
        Loading event registrations…
      </p>
    )
  if (error && !report)
    return <Alert className="mt-6">Could not load event registrations. {error.message}</Alert>
  if (!report) return null
  return (
    <>
      {error && (
        <Alert className="mt-6">Could not update event registrations. {error.message}</Alert>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-body-sm">
        <span>{report.statement}</span>
        {report.comparison_label && (
          <span className="text-muted-foreground">{report.comparison_label}</span>
        )}
        {report.previous_edition === 'none' && (
          <EventDetailsLink eventId={report.event.id} onOpenEvent={onOpenEvent} size="sm">
            Set previous edition
          </EventDetailsLink>
        )}
      </div>
      {report.empty_message ? (
        <div className="mt-6">
          <EmptyState title={report.empty_message} />
        </div>
      ) : (
        <section aria-label="Registration results" aria-busy={updating}>
          <RegistrationSummary figures={report.summary} />
          <RegistrationChart report={report} />
          <RegistrationToolbar report={report} params={params} update={update} />
          {updating && (
            <p role="status" className="mt-3 text-caption text-muted-foreground">
              Updating event registrations…
            </p>
          )}
          <RegistrationTable report={report} params={params} update={update} />
        </section>
      )}
    </>
  )
}

function EventDetailsLink({
  eventId,
  onOpenEvent,
  children,
  size,
}: { eventId: string; children: ReactNode; size?: 'sm' } & EventNavigation) {
  return (
    <Button variant="outline" size={size} asChild>
      <a
        href={`/events/manage/${encodeURIComponent(eventId)}`}
        onClick={(event) => {
          if (
            !onOpenEvent ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          )
            return
          event.preventDefault()
          onOpenEvent(Number(eventId))
        }}
      >
        {children}
      </a>
    </Button>
  )
}
